import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  Typography,
  Box,
  Paper,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator as DragIcon,
  Minimize as MinimizeIcon,
  Maximize as MaximizeIcon
} from '@mui/icons-material';
import { useAuthContext } from '../backend/AuthContext.js';
import { UserType } from '@shared/User';
import { useAlertContext } from '../backend/AlertContext.js';
import api from '../backend/Axios.js';

const tutorialSteps = [
  {
    title: 'Welcome to the Facilitator Tutorial',
    content: `This is a sandbox environment where you can safely explore and learn the facilitator tools without affecting any real data.\n\nOnce you leave the Facilitators group, you will no longer be in the sandbox environment and all changes are permanent.`,
    action: 'Get Started'
  },
  {
    title: 'Overview',
    content: `The left panel ("Users") shows all of the participants in this cohort.\n\nThe right panels show scheduled events and any announcements.`,
    action: 'Get Started'
  },
  {
    title: 'Filter Events',
    content: 'Use the filter controls ("Show...") at the top-right of the Deadlines and Group Meetings panel to filter events by type or date. Try filtering events by "Kickoff" type.',
    action: 'Try Filtering'
  },
  {
    title: 'Edit an Event',
    content: 'Click on the "Details" button to edit an event. You can change the name, description, date, or other details.\n\nTry changing an event\'s name and then saving it.',
    action: 'Edit Event'
  },
  {
    title: 'Create a New Event',
    content: 'Click the "+ New Event" button to create a new event. Fill in the details and save. This is how you would add meetings to your cohort.\n\nParticipants can sign up for any events unless they are the "deadline" type.',
    action: 'Create Event'
  },
  {
    title: 'Delete or Duplicate an Event',
    content: 'When you are in the event details page, you can also delete or duplicate the event.\n\nDon\'t worry - this won\'t affect real data in sandbox mode.\n\nTry out each and see what happens.',
    action: 'Delete or Duplicate Event'
  },
  {
    title: 'Attendance',
    content: 'While in the event details page, you can mark participants as present or absent for an event.\n\nYou can also add participants to an event.\n\nEach particpant\'s most recent session reflection is at the bottom of the page.\n\nA participant\'s attendance contributes to their certificate progress.\n\nTo view these features, choose an event in the past.',
    action: 'Mark Attendance'
  },
  {
    title: 'Email Meeting Participants',
    content: 'While in the event details page, you can email all participants signed up for the event from the "Email Participants" dropdown menu.',
    action: 'Email Meeting Participants'
  },
  {
    title: 'Announcements Visibility',
    content: 'In the bottom-right panel you can filter, edit, create, and delete announcements just like events.\n\nAnnouncements appear on a participant\'s main page.\n\nYou can choose which ones are visible by clicking the "Show/Hide" button.',
    action: 'Show/Hide Announcements'
  },
  {
    title: 'Timed Announcements',
    content: 'You can manually make an announcement visible or you can set it to be visible for a specific duration.\n\nThis is useful for sending reminders or important information to participants.\n\nScroll down an announcement that has dates and try editing it.',
    action: 'Edit Timed Announcement'
  },
  {
    title: 'Ordering Announcements',
    content: 'You can reorder announcements by dragging and dropping them.\n\nThis controls the order in which they appear on a participant\'s main page.',
    action: 'Reorder Announcements'
  },
  {
    title: 'User Profiles',
    content: 'In the "Users" section, you can view all users in the cohort, some basic information, and their certificate progress.\n\nClick on the "View" button to see an individual user\'s profile.',
    action: 'View Users'
  },
  {
    title: 'Edit User Information',
    content: 'From a user\'s profile, you can edit their information and see more details about them. In addition, you add or delete their partners.\n\nAt the bottom of the page is all of their progress in peer coaching... registration, surveys, session reflections, and meeting attendance.\n\nFeel free to edit their information.',
    action: 'Edit User Info'
  },
  {
    title: 'Email Participant',
    content: 'You can email participants from the "Users" section.\n\nClick on the "Contact" button to send an email to a specific participant.\n\nGroup emails will be discussed later.',
    action: 'Email Participant'
  },
  {
    title: 'Top Menu',
    content: 'There are many features in the top menu... the Discussion section, Surveys, Settings, Reports, Matching, the Alumni network, and a user\'s view.\n\nYou can also view your own profile in the top right.',
    action: 'Review Top Menu'
  },
  {
    title: 'Explore Discussions',
    content: 'The Discussions section shows forum-style conversations. You can moderate and participate in discussions here.\n\nParticipants can ask questions and share their goals here.',
    action: 'View Discussions'
  },
  {
    title: 'Surveys',
    content: 'The Surveys section shows all of the surveys... registration, session reflections, matching, program evaluation, and more.\n\nYou can edit any of the surveys in this section.',
    action: 'View Surveys'
  },
  {
    title: 'Reports',
    content: 'The Reports section is where you can view all participant data... certificate progress, participation, openness comparisons, survey results, and more.',
    action: 'View Reports'
  },
  {
    title: 'Group Emails',
    content: 'On the Reports page, you can award certificates and send emails to custom groups of participants.\n\nTry sending an email to a group of participants that you\'ve selected by checking the boxes in their row.\n\nYou can also send group emails from the "Users" section by clicking "Email Group". If you choose an option in the "Show..." dropdown, it will send a group email to only those participants.',
    action: 'Group Emails'
  },
  {
    title: 'Settings',
    content: 'The Settings page is where you can configure group and cohort settings, including start dates, deadlines, and more.\n\nYou can also search for users in ANY cohort or group by name or email in the "General" section.',
    action: 'View Settings'
  },
  {
    title: 'Edit Pages and Emails',
    content: 'On the Settings page, you can also edit the text of various site pages and the automated emails that participants receive.',
    action: 'Edit Pages and Emails'
  },
  {
    title: 'Alumni Network',
    content: 'The Alumni page is where you can see all of the participants who have completed the program, received their certificate, and joined the alumni network.',
    action: 'View Alumni Network'
  },
  {
    title: 'User Matching',
    content: 'The Matching page allows you to pair participants based on their preferences and responses. This is a key facilitator function.',
    action: 'View Matching'
  },
  {
    title: 'Matching Results',
    content: 'The Matching process has three parts to it...\n\n-- Load users: brings in participants from this cohort and linked cohorts.\n\n-- Match users: uses an algorithm to pair participants based on their preferences and responses.\n\n-- Send emails: sends emails to participants who have been matched.',
    action: 'View Matching Results'
  },
  {
    title: 'Tutorial Complete',
    content: 'You\'ve completed the tutorial! You can now confidently use all the facilitator tools.',
    action: 'Finish'
  }
];

interface TutorialProps {
  show?: boolean;
  onClose?: () => void;
}

export const Tutorial = ({ show = false, onClose }: TutorialProps) => {
  const { user, setUser } = useAuthContext();
  const {setAlert} = useAlertContext();
  const [tutorialStep, setTutorialStep] = useState(0);
  const [showTutorial, setShowTutorial] = useState(show);
  const [isMinimized, setIsMinimized] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const tutorialRef = useRef<HTMLDivElement>(null);

  const handleNext = (): void => {
    if (tutorialStep < tutorialSteps.length - 1) {
      setTutorialStep(tutorialStep + 1);
    } else {
      setUser({...user, tutorial: true} as UserType);
      api.put('/api/shared/user_info', {
        id: user?.id,
        key: 'tutorial',
        value: true
      })
      .then(() => {
        setAlert({open: true, message: 'Tutorial complete!', type: 'success'});
        handleClose();
      })
      .catch((err) => {
        setAlert({open: true, message: 'Error completing tutorial', type: 'error'});
      });
    }
  };

  const handlePrevious = (): void => {
    if (tutorialStep > 0) {
      setTutorialStep(tutorialStep - 1);
    }
  };

  const handleClose = (): void => {
    setShowTutorial(false);
    onClose?.();
  };

  const handleMinimize = (): void => {
    setIsMinimized(!isMinimized);
  };

  const handleMouseDown = (e: React.MouseEvent): void => {
    if (tutorialRef.current) {
      const rect = tutorialRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  };

  const handleMouseMove = (e: MouseEvent): void => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  };

  const handleMouseUp = (): void => {
    setIsDragging(false);
  };

  useEffect(() => {
    setShowTutorial(show);
  }, [show]);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const currentStep = tutorialSteps[tutorialStep];

  if (!showTutorial) return null;

  return (
    <Paper
      ref={tutorialRef}
      elevation={8}
      sx={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        width: 320,
        maxWidth: '90vw',
        zIndex: 1300,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.main',
        cursor: isDragging ? 'grabbing' : 'default',
        userSelect: 'none'
      }}
    >
      {/* Header with drag handle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' }
        }}
        onMouseDown={handleMouseDown}
      >
        <DragIcon sx={{ mr: 1, fontSize: 18 }} />
        <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 600 }}>
          Tutorial ({tutorialStep + 1}/{tutorialSteps.length})
        </Typography>
        <IconButton
          size="small"
          onClick={handleMinimize}
          sx={{ color: 'inherit', mr: 0.5 }}
        >
          {isMinimized ? <MaximizeIcon fontSize="small" /> : <MinimizeIcon fontSize="small" />}
        </IconButton>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{ color: 'inherit' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Collapse in={!isMinimized}>
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {currentStep.title}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
            {currentStep.content}
          </Typography>
          
          {tutorialStep === 0 && (
            <Box sx={{
              p: 1.5,
              mb: 2,
              bgcolor: 'info.light',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'info.main'
            }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 500 }}>
                🏖️ Sandbox Mode: Safe tutorial environment
              </Typography>
            </Box>
          )}

          {/* Navigation buttons */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
            <Button
              size="small"
              onClick={handlePrevious}
              disabled={tutorialStep === 0}
              variant="outlined"
            >
              Previous
            </Button>
            <Button
              size="small"
              onClick={handleNext}
              variant="contained"
              color="primary"
            >
              {tutorialStep === tutorialSteps.length - 1 ? 'Finish' : 'Next'}
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};
